import type { ExpenseInput } from "./index";
export type ReceiptCategory = { category: string; subcategory: string };
export type ReceiptAdjustment = ReceiptCategory & {
  id: string;
  label: string;
  kind: "discount" | "tax" | "fee" | "other";
  /** Signed decimal amount: a discount is negative. */
  amount: string;
  included: boolean;
  target: "all" | "items" | "category";
  itemIds: string[];
};
export type ReceiptSplitPlan = {
  mode: "off" | "items" | "manual";
  assignments: Record<string, ReceiptCategory>;
  adjustments: ReceiptAdjustment[];
};
export type ReceiptCategoryTotal = ReceiptCategory & {
  items: number;
  discount: number;
  tax: number;
  fee: number;
  other: number;
  total: number;
};
export const units = [
  "",
  "each",
  "dozen",
  "sheet",
  "roll",
  "g",
  "kg",
  "ml",
  "l",
] as const;
export type ReceiptItem = {
  id: string;
  original: string;
  name: string;
  brand: string;
  group: string;
  quantity: string;
  total: string;
  pack: string;
  measure: string;
  unit: string;
};
export type ReceiptDraft = {
  id: string;
  fingerprint: string;
  expense: ExpenseInput;
  currency: string;
  reference: string;
  adjustments: string;
  warning: string;
  items: ReceiptItem[];
  categorySplit?: ReceiptSplitPlan;
  /** The photo(s) behind this draft, already uploaded to R2 during the scan step (see
   * `scanReceipt`) so the client never has to hold or persist the image bytes themselves through
   * review — only this small reference, attached to the receipt once it's actually saved. The
   * server strips this on the way back in (`validateReceipt`), so it never affects the save itself. */
  pendingImages?: {
    key: string;
    mimeType: string;
    byteSize: number;
    width: number;
    height: number;
    sha256: string;
    thumbnailKey?: string;
  }[];
};
/** One item from a receipt, paired with whether its price has been recorded yet. */
export type Tracking = { itemId: string; item: ReceiptItem; saved: boolean };

export type SavedReceipt = {
  id?: string;
  revision?: string;
  receipt: ReceiptDraft | null;
  tracking: Tracking[];
  images?: string[];
};
/** Reviewed metadata is saved atomically with the transaction's category allocations. */
export type ReceiptReviewChange = {
  id: string;
  expectedRevision: string;
  categorySplit: ReceiptSplitPlan;
  itemTotals: Record<string, string>;
};
export type TransactionReceiptEdit = {
  original: ReceiptDraft;
  draft: ReceiptDraft;
  revision: string;
};
export type TransactionReceipts = SavedReceipt & { receipts?: SavedReceipt[] };

export type Product = {
  id: string;
  name: string;
  brand: string;
  group: string;
};
export type PriceObservation = Product & {
  observationId: string;
  /** Stable create identity, when the backend records one for retry recovery. */
  requestId?: string;
  revision?: string;
  date: string;
  /** The shop, as a payee row. `payeeName` is that payee's name, kept for display. */
  payeeId?: string;
  payeeName: string;
  currency: string;
  total: number;
  measure: number | null;
  unit: string;
  unitPrice: number | null;
  pack: string;
  transactionId: string;
  /** The date of the transaction the price was read off, which can differ from `date`; empty for a standalone check. */
  transactionDate?: string;
  accountId?: string;
  category?: string;
  notes?: string;
  receiptId?: string;
};
export function normalizedMeasure(measure: string, unit: string) {
  const value = Number(measure);
  if (!measure || !unit || !Number.isFinite(value) || value <= 0) return null;
  return {
    measure: unit === "dozen" ? value * 12 : unit === "g" || unit === "ml" ? value / 1000 : value,
    unit: unit === "dozen" ? "each" : unit === "g" ? "kg" : unit === "ml" ? "l" : unit,
  };
}
export function receiptSummary(draft: ReceiptDraft) {
  const lines = draft.items.map(
    (i) =>
      `${i.quantity || "?"} × ${i.original}: ${i.total || "?"} ${draft.currency}`,
  );
  const heading = `Receipt${draft.reference ? " " + draft.reference : ""}`;
  return [
    heading,
    ...lines,
    draft.adjustments,
    `Total: ${draft.expense.amount} ${draft.currency}`,
  ]
    .filter(Boolean)
    .join("\n");
}
